import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import bwipjs from "bwip-js";
import Header from "./header";

const STATUS_OPTIONS = [
  "A préparer",
  "Non Livrée",
  "picked",
  "Dispatched",
  "sav",
];

const OPERATOR_OPTIONS = [
  { value: "gte", label: ">=" },
  { value: "gt", label: ">" },
  { value: "lte", label: "<=" },
  { value: "lt", label: "<" },
];

const SORT_BY_OPTIONS = [
  { value: "cl_code", label: "Shop" },
  { value: "en_num_doc", label: "Num cmd Danielli" },
];

const getMongoStockFilter = (operator, value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return { $gte: 100 };

  if (operator === "gt") return { $gt: numericValue };
  if (operator === "lt") return { $lt: numericValue };
  if (operator === "lte") return { $lte: numericValue };
  return { $gte: numericValue };
};

const isStockMatching = (stock, operator, value) => {
  const currentStock = Number(stock);
  const compareValue = Number(value);
  if (Number.isNaN(currentStock) || Number.isNaN(compareValue)) return false;

  if (operator === "gt") return currentStock > compareValue;
  if (operator === "lt") return currentStock < compareValue;
  if (operator === "lte") return currentStock <= compareValue;
  return currentStock >= compareValue;
};

const getDatamatrixSrc = (value) => {
  if (!value) return "";
  const canvas = document.createElement("canvas");
  bwipjs.toCanvas(canvas, {
    bcid: "datamatrix",
    text: String(value),
    scale: 2,
    height: 8,
    includetext: false,
  });
  return canvas.toDataURL("image/png");
};

const OrdersStockPrint = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("A préparer");
  const [stockOperator, setStockOperator] = useState("gte");
  const [stockValue, setStockValue] = useState(100);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("cl_code");

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const page = 1;
      const payload = {
        code: undefined,
        available_stock: getMongoStockFilter(stockOperator, stockValue),
        pagination: false,
        search: search || undefined,
        sort_by: sortBy,
        sort: "ASC",
        etat_lib: status,
        archive: false,
        page,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/store/get-data-code/available-stock/${page}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("amira-token")}`,
          },
        }
      );

      setOrders(response?.data?.data || []);
    } catch (err) {
      console.log(err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const statusOk = status ? order?.etat_lib === status : true;
      const stockOk = isStockMatching(order?.avail_stock, stockOperator, stockValue);
      return statusOk && stockOk;
    });
  }, [orders, status, stockOperator, stockValue]);

  return (
    <div className="orders-print-page">
      <Header />

      <div className="orders-print-controls no-print mx-5 my-3">
        <h5 className="mb-3">Printable Orders (A4)</h5>
        <div className="d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label className="form-label mb-1">Status</label>
            <select
              className="form-control"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label mb-1">Sort by</label>
            <select
              className="form-control"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_BY_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label mb-1">Stock Filter</label>
            <select
              className="form-control"
              value={stockOperator}
              onChange={(e) => setStockOperator(e.target.value)}
            >
              {OPERATOR_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label mb-1">Stock %</label>
            <input
              type="number"
              className="form-control"
              value={stockValue}
              onChange={(e) => setStockValue(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label mb-1">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Order/client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button className="btn btn-bg text-white" onClick={fetchOrders}>
            Apply Filters
          </button>
          <button className="btn btn-outline-secondary" onClick={() => window.print()}>
            Print A4
          </button>
        </div>
      </div>

      <div className="a4-sheet">
        {loading ? (
          <div className="text-center p-5">Loading...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center p-5">No printable orders found.</div>
        ) : (
          <div className="print-list">
            {filteredOrders.map((item, index) => (
              <div className="print-list-row" key={item?._id || `${item?.en_num_doc}-${index}`}>
                <div className="print-list-title">
                  {(item?.cl_code || "").toUpperCase()} - {item?.en_num_doc}
                </div>
                <div className="print-list-code">
                  <img src={getDatamatrixSrc(item?.en_num_doc)} alt={item?.en_num_doc || "datamatrix"} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersStockPrint;
